"use client";

import NavigationArrow from "@/assets/NavigationArrow";
import { cn } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import React, { useCallback, useEffect, useRef, useState } from "react";

const TWEEN_FACTOR_BASE = 0.5;

const numberWithinRange = (number: number, min: number, max: number) =>
  Math.min(Math.max(number, min), max);

interface EmblaCarouselProps {
  children: React.ReactNode[];
  options?: any;
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  onNavigationClick?: (
    direction: "prev" | "next",
    currentIndex: number
  ) => void;
}

const EmblaCarousel: React.FC<EmblaCarouselProps> = ({
  children,
  options,
  left_navigation_icon,
  left_navigation_icon_alt,
  right_navigation_icon,
  right_navigation_icon_alt,
  onNavigationClick,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    slidesToScroll: 1,
    containScroll: false,
    ...options,
  });
  const tweenFactor = useRef(0);
  const tweenNodes = useRef<HTMLDivElement[]>([]);

  const setTweenNodes = useCallback(() => {
    if (!emblaApi) return;
    const realSlides = emblaApi.slideNodes().slice(0, children.length);

    tweenNodes.current = realSlides.map(
      (slideNode: HTMLElement) =>
        slideNode.querySelector(".embla__slide__card") as HTMLDivElement
    );
  }, [emblaApi]);

  const setTweenFactor = useCallback(() => {
    if (!emblaApi) return;
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, [emblaApi]);

  const tweenScale = useCallback(() => {
    if (!emblaApi) return;
    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();

    emblaApi.scrollSnapList().forEach((snap: number, snapIndex: number) => {
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex: number) => {
        const slideNode = tweenNodes.current[slideIndex];
        if (!slideNode) return;

        let diffToTarget = snap - scrollProgress;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem: any) => {
            const target = loopItem.target();
            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);
              if (sign === -1) diffToTarget = snap - (1 + scrollProgress);
              if (sign === 1) diffToTarget = snap + (1 - scrollProgress);
            }
          });
        }

        const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
        const scale = numberWithinRange(tweenValue, 0.85, 1.1);
        slideNode.style.transform = `scale(${scale})`;
        slideNode.style.transition =
          "transform 0.3s ease-out, opacity 0.3s ease-out";
        slideNode.style.zIndex = `${Math.round(scale * 100)}`;
      });
    });
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setTweenNodes();
    setTweenFactor();
    tweenScale();
    setCurrentIndex(emblaApi.selectedScrollSnap());

    emblaApi
      .on("reInit", () => {
        setTweenNodes();
        setTweenFactor();
        tweenScale();
        setCurrentIndex(emblaApi.selectedScrollSnap());
      })
      .on("scroll", () => tweenScale())
      .on("select", () => {
        tweenScale();
        setCurrentIndex(emblaApi.selectedScrollSnap());
      });
  }, [emblaApi, setTweenNodes, setTweenFactor, tweenScale]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      const newIndex = emblaApi.selectedScrollSnap();
      setCurrentIndex(newIndex);
      onNavigationClick?.("prev", newIndex);
    }
  }, [emblaApi, onNavigationClick]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      const newIndex = emblaApi.selectedScrollSnap();
      setCurrentIndex(newIndex);
      onNavigationClick?.("next", newIndex);
    }
  }, [emblaApi, onNavigationClick]);

  useEffect(() => {
    if (emblaApi && children.length === 3) {
      emblaApi.scrollNext();
    }
  }, [emblaApi, children.length]);

  return (
    <div className="w-full relative mx-auto">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex py-3 ">
          {React.Children.map(children, (child, index) => (
            <div
              className={cn(
                "embla__slide flex justify-center items-center",
                "flex-[0_0_100%]",
                "sm:flex-[0_0_33.333%]"
              )}
              key={index}
            >
              <div className="embla__slide__card w-full px-3">{child}</div>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={scrollPrev}
        className={cn(
          "absolute left-0 top-1/2 -translate-y-1/2 cursor-pointer transition-transform hover:scale-110 z-10",
          "xs:left-[-10px]",
          "sm:left-[-20px]",
          "lg:left-[-30px]",
          "xl:left-[-56px]"
        )}
        aria-label="Previous slide"
      >
        {left_navigation_icon ? (
          <div
            className={cn(
              "w-[36px] h-[36px]",
              "sm:w-[38px] sm:h-[38px]",
              "md:w-[42px] md:h-[42px]",
              "lg:w-[52px] lg:h-[52px]",
              "xl:w-[52px] xl:h-[52px]",
              emblaApi?.canScrollPrev() ? "opacity-100" : "opacity-30"
            )}
          >
            <Image
              src={left_navigation_icon}
              alt={left_navigation_icon_alt || "Previous"}
              width={100}
              height={100}
              className="w-full h-full object-contain"
            />
          </div>
        ) : (
          <NavigationArrow
            className="xs:size-9 sm:size-[38px] lg:size-[52px] rotate-180"
            fill="#f74958"
            pathFill="#F8F9FA"
          />
        )}
      </button>
      <button
        onClick={scrollNext}
        className={cn(
          "absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer transition-transform hover:scale-110 z-10",
          "xs:right-[-10px]",
          "sm:right-[-20px]",
          "lg:right-[-30px]",
          "xl:right-[-56px]"
        )}
        aria-label="Next slide"
      >
        {right_navigation_icon ? (
          <div
            className={cn(
              "w-[36px] h-[36px]",
              "sm:w-[38px] sm:h-[38px]",
              "md:w-[42px] md:h-[42px]",
              "lg:w-[52px] lg:h-[52px]",
              "xl:w-[52px] xl:h-[52px]",
              emblaApi?.canScrollNext() ? "opacity-100" : "opacity-30"
            )}
          >
            <Image
              src={right_navigation_icon}
              alt={right_navigation_icon_alt || "Next"}
              width={100}
              height={100}
              className="w-full h-full object-contain"
            />
          </div>
        ) : (
          <NavigationArrow
            fill="#f74958"
            pathFill="#F8F9FA"
            className="xs:size-9 sm:size-[38px] lg:size-[52px]"
          />
        )}
      </button>
    </div>
  );
};

export default EmblaCarousel;
